import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { isExcludedAnalyticsEvent } from '../lib/analyticsBotFilter';

const WINDOW_MS = 5 * 60 * 1000;
const REFRESH_MS = 30 * 1000;

export interface LiveWebsiteVisit {
  path: string;
  timeLabel: string;
}

export interface LiveWebsiteVisitors {
  loading: boolean;
  /** Distinct sessions with a page view in the last 5 minutes. */
  count: number;
  /** Newest page view inside that window. */
  latest: LiveWebsiteVisit | null;
}

const EMPTY: LiveWebsiteVisitors = { loading: false, count: 0, latest: null };

function pagePath(pageUrl: string | null): string {
  if (!pageUrl) return '/';
  try {
    const path = new URL(pageUrl).pathname;
    return path || '/';
  } catch {
    return pageUrl.startsWith('/') ? pageUrl : '/';
  }
}

function timeLabel(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Live website presence from `analytics_events` page views.
 * No location lookup — path and time only.
 */
export function useLiveWebsiteVisitors(
  businessId: string | null | undefined,
  enabled: boolean,
): LiveWebsiteVisitors {
  const [state, setState] = useState<LiveWebsiteVisitors>({ ...EMPTY, loading: enabled });

  useEffect(() => {
    if (!enabled || !businessId) {
      setState(EMPTY);
      return;
    }

    let cancelled = false;
    let loadedOnce = false;

    const load = async () => {
      if (!loadedOnce) setState((current) => ({ ...current, loading: true }));
      const since = new Date(Date.now() - WINDOW_MS).toISOString();
      const { data, error } = await supabase
        .from('analytics_events')
        .select('timestamp, session_id, visitor_id, page_url, user_agent, referrer')
        .eq('business_id', businessId)
        .eq('event_type', 'page_view')
        .gte('timestamp', since)
        .order('timestamp', { ascending: false })
        .limit(500);

      if (cancelled) return;
      loadedOnce = true;

      if (error || !data) {
        setState({ loading: false, count: 0, latest: null });
        return;
      }

      const people = data.filter((row) => !isExcludedAnalyticsEvent(row));
      const sessions = new Set<string>();
      for (const row of people) {
        const key = row.session_id || row.visitor_id;
        if (key) sessions.add(key);
      }

      const newest = people[0];
      setState({
        loading: false,
        count: sessions.size,
        latest: newest
          ? { path: pagePath(newest.page_url), timeLabel: timeLabel(newest.timestamp) }
          : null,
      });
    };

    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [businessId, enabled]);

  return state;
}
